import {
  BookmarkX,
  Camera,
  Check,
  FileText,
  Link as LinkIcon,
  NotebookPen,
  Quote,
  Sparkles,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MockRecipeCard } from "./MockRecipeCard";
import { Lemon, Tomato, Herb, Whisk, Garlic, Chilli, Bowl } from "./FoodMotifs";
import { PlateMark } from "@/components/PlateMark";
import messyKitchen from "@/assets/landing/messy-kitchen.jpg";
import platedDish from "@/assets/landing/plated-dish.jpg";
import cookingHands from "@/assets/landing/cooking-hands.jpg";

const SectionHeading = ({
  eyebrow,
  title,
  sub,
}: {
  eyebrow?: string;
  title: string;
  sub?: string;
}) => (
  <div className="mx-auto mb-12 max-w-2xl text-center animate-fade-up">
    {eyebrow && (
      <p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
        {eyebrow}
      </p>
    )}
    <h2 className="font-display text-3xl font-semibold leading-tight text-foreground sm:text-4xl md:text-5xl">
      {title}
    </h2>
    {sub && (
      <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
        {sub}
      </p>
    )}
  </div>
);

/* ---------------- Problem ---------------- */
export const Problem = () => {
  const items = [
    { icon: Camera, label: "Screenshots you never revisit" },
    { icon: FileText, label: "Printed recipes buried in drawers" },
    { icon: NotebookPen, label: "Notes apps full of half-saved ideas" },
    { icon: BookmarkX, label: "Bookmarks you forget exist" },
  ];
  return (
    <section className="bg-warm-sage">
      <div className="mx-auto max-w-5xl px-6 py-20 md:py-28">
        <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-center">
          <div>
            <SectionHeading
              eyebrow="The problem"
              title="Recipes everywhere. Usable nowhere."
            />
            <div className="grid gap-4 sm:grid-cols-2">
              {items.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-4 rounded-2xl bg-card p-5 shadow-soft ring-1 ring-border"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tomato/15 text-tomato">
                    <Icon className="h-5 w-5" />
                  </span>
                  <p className="text-base text-foreground">{label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative mx-auto w-full max-w-sm">
            <img
              src={messyKitchen}
              alt="A messy kitchen counter with handwritten notes, sticky notes and a phone showing a recipe"
              loading="lazy"
              width={1024}
              height={768}
              className="aspect-[4/3] w-full rotate-2 rounded-2xl object-cover shadow-card ring-1 ring-border"
            />
            <Chilli className="absolute -right-3 -top-3 h-12 w-12 -rotate-12 text-tomato" />
            <Garlic className="absolute -bottom-3 -left-3 h-12 w-12 rotate-6 text-mustard" />
          </div>
        </div>
        <p className="mx-auto mt-12 max-w-xl text-center font-display text-2xl font-medium leading-snug text-foreground sm:text-3xl">
          You don&rsquo;t need more recipes. You need better ones.
        </p>
      </div>
    </section>
  );
};

/* ---------------- Solution ---------------- */
export const Solution = () => {
  const items = [
    {
      icon: Upload,
      title: "Upload or paste",
      body: "A photo, a screenshot, a PDF, or a link.",
      tone: "mustard" as const,
      Motif: Lemon,
    },
    {
      icon: Sparkles,
      title: "We extract & clean",
      body: "Ingredients, steps & structure — sorted in seconds.",
      tone: "tomato" as const,
      Motif: Tomato,
    },
    {
      icon: PlateMark,
      title: "Cook from it",
      body: "A clear, readable recipe you can actually follow.",
      tone: "basil" as const,
      Motif: Herb,
    },
  ];
  const toneClasses = {
    mustard: "bg-mustard/15 text-mustard",
    tomato: "bg-tomato/15 text-tomato",
    basil: "bg-basil/15 text-basil",
  };
  const motifTone = {
    mustard: "text-mustard/30",
    tomato: "text-tomato/30",
    basil: "text-basil/30",
  };
  return (
    <section>
      <div className="mx-auto max-w-5xl px-6 py-20 md:py-28">
        <SectionHeading eyebrow="The fix" title="CleanPlate fixes the chaos" />
        <div className="grid gap-6 md:grid-cols-3">
          {items.map(({ icon: Icon, title, body, tone, Motif }) => (
            <div
              key={title}
              className="relative overflow-hidden rounded-2xl bg-card p-6 text-center shadow-soft ring-1 ring-border"
            >
              <Motif className={`absolute -right-3 -top-3 h-16 w-16 rotate-12 ${motifTone[tone]}`} />
              <span className={`relative mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${toneClasses[tone]}`}>
                <Icon className="h-6 w-6" />
              </span>
              <h3 className="relative font-display text-xl font-semibold text-foreground">
                {title}
              </h3>
              <p className="relative mt-2 text-sm text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Showcase ---------------- */
export const Showcase = () => {
  return (
    <section className="bg-warm-tomato">
      <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
        <SectionHeading
          eyebrow="What you get"
          title="Designed to be read, not deciphered"
          sub="Every recipe comes back in the same calm, scannable format — the way a cookbook would write it."
        />
        <div className="grid items-center gap-8 lg:grid-cols-[1fr_1.4fr]">
          <div className="relative mx-auto w-full max-w-sm lg:max-w-none">
            <img
              src={platedDish}
              alt="A plated pasta dish topped with fresh basil and parmesan on a wooden table"
              loading="lazy"
              width={1024}
              height={1024}
              className="aspect-square w-full -rotate-2 rounded-2xl object-cover shadow-card ring-1 ring-border"
            />
            <Bowl className="absolute -bottom-4 -right-4 h-14 w-14 text-basil" />
            <Lemon className="absolute -left-4 -top-4 h-12 w-12 rotate-12 text-mustard" />
          </div>
          <MockRecipeCard />
        </div>
      </div>
    </section>
  );
};

/* ---------------- Benefits ---------------- */
export const Benefits = () => {
  const items = [
    "Everything in one place",
    "Consistent format every time",
    "Easy to read while cooking",
    "No clutter, no waffle",
    "Built for real kitchens, not food blogs",
  ];
  return (
    <section className="mx-auto max-w-3xl px-6 py-20 md:py-28">
      <SectionHeading eyebrow="Why it works" title="Built for real cooking" />
      <div className="relative">
        <Whisk className="absolute -left-6 -top-6 hidden h-16 w-16 -rotate-12 text-mustard/40 sm:block" />
        <Herb className="absolute -bottom-6 -right-6 hidden h-16 w-16 rotate-12 text-basil/40 sm:block" />
        <ul className="relative space-y-3 rounded-2xl bg-card p-6 shadow-soft ring-1 ring-border sm:p-8">
          {items.map((it) => (
            <li key={it} className="flex items-center gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-basil/15 text-basil">
                <Check className="h-4 w-4" />
              </span>
              <span className="text-base text-foreground">{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

/* ---------------- Save + organise ---------------- */
export const SaveOrganise = ({ onCta }: { onCta: () => void }) => {
  return (
    <section className="bg-warm-sage">
      <div className="relative mx-auto max-w-3xl px-6 py-20 text-center md:py-28">
        <Garlic className="absolute left-6 top-10 hidden h-14 w-14 -rotate-12 text-basil/40 md:block" />
        <Tomato className="absolute right-6 bottom-10 hidden h-14 w-14 rotate-6 text-tomato/40 md:block" />
        <SectionHeading
          eyebrow="Save + organise"
          title="Save what you actually use"
          sub="Save up to 5 recipes free, search them in a tap, & build your own clean collection over time."
        />
        <Button onClick={onCta} size="lg" className="h-12 px-8">
          Save your first recipe
        </Button>
      </div>
    </section>
  );
};

/* ---------------- Social proof ---------------- */
export const SocialProof = () => {
  const quotes = [
    { text: "This is the first time I've actually reused a recipe I saved.", Motif: Lemon, tone: "text-mustard/50" },
    { text: "I didn't realise how messy my recipes were until this cleaned them.", Motif: Herb, tone: "text-basil/50" },
  ];
  return (
    <section className="mx-auto max-w-5xl px-6 py-20 md:py-28">
      <div className="grid gap-6 md:grid-cols-2">
        {quotes.map(({ text, Motif, tone }) => (
          <figure
            key={text}
            className="relative rounded-2xl bg-card p-8 shadow-soft ring-1 ring-border"
          >
            <Quote className="mb-4 h-6 w-6 text-tomato" />
            <blockquote className="font-display text-xl italic leading-snug text-foreground">
              “{text}”
            </blockquote>
            <div className="mt-4 flex items-center justify-between">
              <figcaption className="text-sm text-muted-foreground">
                — Home cook
              </figcaption>
              <Motif className={`h-8 w-8 ${tone}`} />
            </div>
          </figure>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Final CTA ---------------- */
export const FinalCTA = ({
  onUploadClick,
  onPasteClick,
}: {
  onUploadClick: () => void;
  onPasteClick: () => void;
}) => {
  return (
    <section className="relative overflow-hidden">
      {/* Warm photo background — low opacity so the type stays calm. */}
      <img
        src={cookingHands}
        alt=""
        aria-hidden="true"
        loading="lazy"
        width={1600}
        height={900}
        className="absolute inset-0 h-full w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background/90" />
      <div className="relative mx-auto max-w-3xl px-6 py-24 text-center md:py-32">
        <h2 className="font-display text-3xl font-semibold leading-tight text-foreground sm:text-4xl md:text-5xl">
          Start with one recipe
        </h2>
        <p className="mx-auto mt-4 max-w-md text-base text-muted-foreground sm:text-lg">
          See the difference in seconds.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button onClick={onUploadClick} size="lg" className="h-12 w-full px-8 sm:w-auto">
            <Upload className="h-4 w-4" />
            Upload Recipe
          </Button>
          <Button
            onClick={onPasteClick}
            size="lg"
            variant="outline"
            className="h-12 w-full bg-card/80 px-8 backdrop-blur sm:w-auto"
          >
            <LinkIcon className="h-4 w-4" />
            Paste URL
          </Button>
        </div>
      </div>
    </section>
  );
};
